import { useIsFetching, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { type BaseCourseApi, type ModuleLessonsApi } from "../../models/api";
import { getModulesByCourseId } from "../../services";
import {
  CourseSlice,
  LessonSlice,
  ModuleSlice,
  SlideSlice,
  WorkoutSlice,
  getCourses,
  getLessons,
  getModules,
  getSlides,
  getWorkouts,
  setCourse,
  setLessons,
  setModules,
  setSlides,
  setWorkouts,
} from "../slices/data";

const ENDPOINT_URL = "/api/assessments/learningpath/modules/course/$courseId";

const selectCourseModules = (id: BaseCourseApi["id"]) =>
  createSelector(
    getCourses,
    getModules,
    getLessons,
    getWorkouts,
    getSlides,
    (
      allCourses,
      allModules,
      allLessons,
      allWorkouts,
      allSlides
    ): ModuleLessonsApi[] | null => {
      if (!id) return null;

      const course = allCourses[id];

      if (!course || !("modules" in course)) return null;

      let isValid = true;

      const modules = [];
      let lessons = [];
      let workouts = [];
      let slides = [];

      for (const moduleId of course.modules) {
        const module = allModules[moduleId];
        if (!module || !("lessons" in module)) {
          isValid = false;
          break;
        }
        for (const lessonId of module.lessons) {
          const lesson = allLessons[lessonId];
          if (!lesson || !("workouts" in lesson) || !("slides" in lesson)) {
            isValid = false;
            break;
          }
          for (const workoutId of lesson.workouts) {
            const workout = allWorkouts[workoutId];
            if (!workout) {
              isValid = false;
              break;
            }
            if (!isValid) break;
            workouts.push(workout);
          }
          for (const slideId of lesson.slides) {
            const slide = allSlides[slideId];
            if (!slide) {
              isValid = false;
              break;
            }
            if (!isValid) break;
            slides.push(slide);
          }
          if (!isValid) break;
          lessons.push({ ...lesson, workouts, slides });
          workouts = [];
          slides = [];
        }
        if (!isValid) break;
        modules.push({ id: moduleId, lessons });
        lessons = [];
      }

      if (!isValid) return null;

      return modules;
    }
  );

const onSuccess =
  (data: ModuleLessonsApi[], params: BaseCourseApi["id"]) =>
  async (dispatch) => {
    const moduleIds: ModuleSlice["id"][] = [];

    const modules: ModuleSlice[] = [];
    const lessons: LessonSlice[] = [];
    const workouts: WorkoutSlice[] = [];
    const slides: SlideSlice[] = [];

    for (const module of data) {
      moduleIds.push(module.id);
      modules.push({
        ...module,
        lessons: module.lessons.map(({ id }) => id),
      } as ModuleSlice);
      for (const lesson of module.lessons) {
        lessons.push({
          ...lesson,
          workouts: lesson.workouts.map(({ id }) => id),
          slides: lesson.slides.map(({ id }) => id),
        } as LessonSlice);
        workouts.push(...lesson.workouts);
        slides.push(...lesson.slides);
      }
    }

    const course = { id: params, modules: moduleIds } as CourseSlice;

    batch(() => {
      dispatch(setCourse(course));
      dispatch(setModules(modules));
      dispatch(setLessons(lessons));
      dispatch(setWorkouts(workouts));
      dispatch(setSlides(slides));
    });
  };

export const useCourseModules = (id: BaseCourseApi["id"] | string) => {
  const parsedId = Number.parseInt(id?.toString(), 10);
  const isValidId = Number.isNaN(parsedId) ? false : true;
  const queryKey = [ENDPOINT_URL, parsedId];
  const dispatch = useDispatch();
  const dataInStore = useSelector(selectCourseModules(parsedId));
  const isAlreadyFetching = useIsFetching({ queryKey });
  const shouldRefetch = !dataInStore && !isAlreadyFetching && isValidId;

  const { data, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => getModulesByCourseId(parsedId),
    enabled: shouldRefetch,
    onSuccess: (data) => dispatch(onSuccess(data, parsedId)),
  });

  const courseModules = dataInStore || data;

  useEffect(() => {
    if (shouldRefetch || (!courseModules && isValidId)) {
      refetch();
    }
  }, [refetch, shouldRefetch, isValidId, courseModules]);

  return {
    data: isValidId ? courseModules || null : null,
    isFetching,
  };
};
